// Dues — month view. Prototype-faithful (chitos-prototype screens1.jsx
// CollectionPage): month strip, 4 KPIs, filter rail (status chips + chit
// select + search), cross-chit dues table. Real data via orpc.dues.list
// (fetched without a status filter; status filtering is client-side so the
// chip counts + KPIs are accurate). Voice-reminder + mark-paid preserved.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { current_language } from '../i18n/index.js';
import { current_due_month, format_inr, offset_due_month } from '../lib/format.js';
import { orpc } from '../lib/orpc.js';
import { MarkPaidModal } from '../components/MarkPaidModal.js';
import { BulkImportModal } from '../components/BulkImportModal.js';
import { Icon } from '../components/Icon.js';
import { NotifyMenu } from '../components/NotifyMenu.js';
import { friendly_error } from '../lib/friendly-error.js';

interface Subscriber { id: string; name_ta: string; name_en: string; phone: string }
interface Chit { id: string; name_ta: string; name_en: string }
interface DueRow {
  id: string;
  chit_id: string;
  subscriber_id: string;
  due_month: string;
  expected_amount_paise: number;
  total_paid_paise: number;
  status: 'pending' | 'paid' | 'partial' | 'overdue';
  subscriber: Subscriber | null;
  chit: Chit | null;
}

type StatusKey = 'overdue' | 'pending' | 'partial' | 'paid';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function month_label(due_month: string): string {
  const [y, m] = due_month.split('-');
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  // Full 4-digit year ("Jun 2026") instead of "'26" — the short form was
  // confusing operators about the year cross-over (Dec '25 vs Jan '26 made
  // people think the system was reading a 2025 clock).
  return `${MONTH_NAMES[idx]} ${y ?? ''}`;
}

function initials(name: string | null | undefined): string {
  if (!name) return '··';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

export function DuesPage(): ReactElement {
  const lang = current_language();
  const qc = useQueryClient();
  const current = current_due_month();
  const [due_month, set_due_month] = useState(current);
  // Default selection includes 'partial' so subscribers who paid PART of
  // their dues don't disappear from the rail. Without it, e.g. a member who
  // paid ₹4,000 of a ₹5,000 instalment shows nowhere on the dues page until
  // the operator manually toggles a chip they don't realise exists.
  const [statuses, set_statuses] = useState<StatusKey[]>(['overdue', 'pending', 'partial']);
  const [chit_filter, set_chit_filter] = useState('all');
  const [search, set_search] = useState('');
  const [pay_target, set_pay_target] = useState<DueRow | null>(null);
  const [bulk_open, set_bulk_open] = useState(false);
  const [voice_toast, set_voice_toast] = useState<{ call_id: string; listen_url: string | null; name: string } | null>(null);

  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  // Month strip: −5 … +1 around the current due month.
  const months = useMemo(
    () => [-5, -4, -3, -2, -1, 0, 1].map((off) => ({ off, key: offset_due_month(current, off) })),
    [current],
  );

  // Fetch the whole month (no status filter) so chip counts + KPIs are exact.
  const q = useQuery({
    queryKey: ['dues', due_month],
    queryFn: () => orpc.dues.list({ due_month }),
  });
  const all_rows = (q.data?.rows ?? []) as DueRow[];

  const voice_m = useMutation({
    mutationFn: (row: DueRow) =>
      orpc.voice.place_reminder({ subscription_id: row.id }).then((r) => ({
        call: r,
        name: (lang === 'ta' ? row.subscriber?.name_ta : row.subscriber?.name_en) ?? '',
      })),
    onSuccess: ({ call, name }) => {
      set_voice_toast({ call_id: call.call_id, listen_url: call.listen_url, name });
      setTimeout(() => set_voice_toast(null), 8000);
    },
  });

  const chit_options = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of all_rows) {
      if (r.chit) seen.set(r.chit.id, lang === 'ta' ? r.chit.name_ta : r.chit.name_en);
    }
    return Array.from(seen.entries());
  }, [all_rows, lang]);

  const count_by_status = (s: StatusKey): number => all_rows.filter((r) => r.status === s).length;

  const filtered = all_rows.filter((r) => {
    if (statuses.length && !statuses.includes(r.status as StatusKey)) return false;
    if (chit_filter !== 'all' && r.chit_id !== chit_filter) return false;
    if (search.trim()) {
      const hay = `${r.subscriber?.name_en ?? ''} ${r.subscriber?.name_ta ?? ''} ${r.subscriber?.phone ?? ''}`.toLowerCase();
      if (!hay.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  // KPIs from the full month.
  const outstanding = all_rows
    .filter((r) => r.status !== 'paid')
    .reduce((s, r) => s + Math.max(0, r.expected_amount_paise - r.total_paid_paise), 0);
  const overdue_amt = all_rows
    .filter((r) => r.status === 'overdue')
    .reduce((s, r) => s + Math.max(0, r.expected_amount_paise - r.total_paid_paise), 0);
  const collected = all_rows.reduce((s, r) => s + r.total_paid_paise, 0);
  const rate = collected + outstanding > 0 ? Math.round((collected / (collected + outstanding)) * 100) : 0;

  const sel_off = months.find((m) => m.key === due_month)?.off ?? 0;

  const toggle_status = (s: StatusKey): void =>
    set_statuses((f) => (f.includes(s) ? f.filter((x) => x !== s) : [...f, s]));

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('நிலுவைகள் — மாதவாரியாக', 'Dues — Month View')}</div>
          <div className="h-sub">
            {t('அனைத்து சீட்டுகளிலும் உறுப்பினர்வாரி நிலுவை', 'Member-level dues across all chits')} · {all_rows.length}{' '}
            {t('வரிசைகள்', 'rows')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {filtered.length > 0 && (
            <NotifyMenu
              bulk={{
                kind: 'dues',
                due_month,
                ...(statuses.length > 0 ? { status: statuses } : {}),
                ...(chit_filter !== 'all' ? { chit_id: chit_filter } : {}),
                ...(search.trim() ? { search: search.trim() } : {}),
              }}
              estimated_count={filtered.length}
              intent="due_reminder"
              variant="inline"
            />
          )}
          <button className="btn" onClick={() => set_bulk_open(true)}>
            <Icon.download /> {t('மொத்த பதிவு', 'Bulk payments')}
          </button>
        </div>
      </div>

      {bulk_open && (
        <BulkImportModal
          kind="payments"
          on_close={() => set_bulk_open(false)}
          on_done={() => void qc.invalidateQueries({ queryKey: ['dues'] })}
        />
      )}

      {/* Month strip */}
      <div className="month-strip">
        {months.map((mo) => (
          <button
            key={mo.key}
            className={'mstrip' + (mo.key === due_month ? ' on' : '') + (mo.off > 0 ? ' future' : '') + (mo.off === 0 ? ' current' : '')}
            onClick={() => set_due_month(mo.key)}
          >
            <span className="ml">{month_label(mo.key)}</span>
            <span className="mt">
              {mo.off > 0 ? t('வரவிருப்பது', 'Upcoming') : mo.off === 0 ? t('இம்மாதம்', 'This month') : `${Math.abs(mo.off)}${t(' மா முன்', 'mo ago')}`}
            </span>
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi primary">
          <div className="kpi-label">{t('மொத்த நிலுவை', 'Outstanding')}</div>
          <div className="kpi-value">{format_inr(outstanding)}</div>
          <div className="kpi-sub">{all_rows.filter((r) => r.status !== 'paid').length} {t('உறுப்பினர்கள்', 'members')}</div>
        </div>
        <div className="kpi warn">
          <div className="kpi-label">{t('தாமதம்', 'Overdue')}</div>
          <div className="kpi-value">{format_inr(overdue_amt)}</div>
          <div className="kpi-sub">{count_by_status('overdue')} {t('உறுப்பினர்', 'members')}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t('வசூலானது', 'Collected')}</div>
          <div className="kpi-value">{format_inr(collected)}</div>
          <div className="kpi-sub">{count_by_status('paid')} {t('செலுத்தினர்', 'paid')}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t('வசூல் வீதம்', 'Collection rate')}</div>
          <div className="kpi-value">{rate}%</div>
          <div className="kpi-sub mono">{sel_off === 0 ? t('நடந்து கொண்டிருக்கிறது', 'in progress') : sel_off < 0 ? t('முடிந்தது', 'closed') : t('வரவிருப்பது', 'upcoming')}</div>
        </div>
      </div>

      <div className="card">
        {/* Filter rail */}
        <div className="filter-rail">
          <div className="fr-group">
            <span className="fr-lbl">{t('நிலை', 'Status')}</span>
            {/* Partial sits between overdue and paid — its own chip so the
                rail enumerates every status the StatusKey union allows.
                Previously omitted; rows with status='partial' were silently
                excluded from the default filter. */}
            {(['overdue', 'pending', 'partial', 'paid'] as StatusKey[]).map((s) => (
              <button key={s} className={'chip' + (statuses.includes(s) ? ' on' : '')} onClick={() => toggle_status(s)}>
                <span className={'chip-dot ' + s} />{' '}
                {s === 'overdue'
                  ? t('தாமதம்', 'Overdue')
                  : s === 'pending'
                    ? t('நிலுவை', 'Pending')
                    : s === 'partial'
                      ? t('பகுதி', 'Partial')
                      : t('செலுத்திய', 'Paid')}
                <span className="ct mono">{count_by_status(s)}</span>
              </button>
            ))}
          </div>
          <div className="fr-divider" />
          <div className="fr-group">
            <span className="fr-lbl">{t('சீட்டு', 'Chit')}</span>
            <select value={chit_filter} onChange={(e) => set_chit_filter(e.target.value)} className="fr-select">
              <option value="all">{t('அனைத்தும்', 'All chits')}</option>
              {chit_options.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div className="fr-divider" />
          <div className="searchbox" style={{ width: 200, margin: 0 }}>
            <Icon.search />
            <input value={search} onChange={(e) => set_search(e.target.value)} placeholder={t('பெயர், ஃபோன்...', 'Name, phone…')} />
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
            {filtered.length} {t('வரிசைகள்', 'rows')}
          </span>
        </div>

        {q.isLoading && <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 14 }}>Loading…</div>}
        {q.error && <div style={{ color: 'var(--bad)', fontSize: 13, padding: 14 }}>{friendly_error(q.error)}</div>}
        {!q.isLoading && filtered.length === 0 && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 24, textAlign: 'center' }}>
            {all_rows.length === 0 ? t('இம்மாதம் நிலுவை இல்லை', 'No dues this month') : t('வடிப்பானுக்கு பொருத்தம் இல்லை', 'No rows match the filters')}
          </div>
        )}
        {filtered.length > 0 && (
          <table className="dues-table">
            <thead>
              <tr>
                <th>{t('உறுப்பினர்', 'Member')}</th>
                <th>{t('சீட்டு', 'Chit')}</th>
                <th style={{ textAlign: 'right' }}>{t('நிலுவை தொகை', 'Due amount')}</th>
                <th>{t('நிலை', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="row-sub">
                      <div className="ava-sm">{initials(r.subscriber?.name_en)}</div>
                      <div>
                        <div className={lang === 'ta' ? 'tamil name-ta' : 'name-en'}>
                          {lang === 'ta' ? r.subscriber?.name_ta : r.subscriber?.name_en}
                        </div>
                        <div className="name-meta mono">{r.subscriber?.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="chit-pill">{lang === 'ta' ? r.chit?.name_ta : r.chit?.name_en}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {(() => {
                      // Partial → show the arithmetic explicitly:
                      //   "₹5,000 − ₹4,000 = ₹1,000"
                      // The "= ₹1,000" is the actual collectable amount today
                      // (what the Mark-paid modal pre-fills). Showing the
                      // subtraction beats a slash-separated pair the operator
                      // has to mentally interpret under collection pressure.
                      //
                      // Pending / overdue → due == expected, so a single
                      // figure is enough. Paid → due == 0 (table filtered
                      // out by default but rendered for the Paid chip).
                      const due = Math.max(
                        0,
                        r.expected_amount_paise - r.total_paid_paise,
                      );
                      if (r.status === 'partial') {
                        return (
                          <span
                            className="amount"
                            style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, whiteSpace: 'nowrap' }}
                            title={t(
                              'மொத்த தொகை − செலுத்தியது = நிலுவை',
                              'Full amount − paid = due',
                            )}
                          >
                            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
                              {format_inr(r.expected_amount_paise)}
                            </span>
                            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>−</span>
                            <span style={{ color: 'var(--good)', fontSize: 11 }}>
                              {format_inr(r.total_paid_paise)}
                            </span>
                            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>=</span>
                            <span style={{ fontWeight: 700 }}>{format_inr(due)}</span>
                          </span>
                        );
                      }
                      if (r.status === 'paid') {
                        return (
                          <span className="amount" style={{ color: 'var(--ink-3)' }}>
                            {format_inr(0)}
                          </span>
                        );
                      }
                      // pending / overdue: nothing paid yet, due == full instalment
                      return (
                        <span className="amount" style={{ fontWeight: 600 }}>
                          {format_inr(due)}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {r.status === 'paid' && <span className="status paid"><span className="dot" />{t('செலுத்தினார்', 'Paid')}</span>}
                    {r.status === 'pending' && <span className="status pending"><span className="dot" />{t('நிலுவை', 'Pending')}</span>}
                    {r.status === 'partial' && <span className="status pending"><span className="dot" />{t('பகுதி', 'Partial')}</span>}
                    {r.status === 'overdue' && <span className="status overdue"><span className="dot" />{t('தாமதம்', 'Overdue')}</span>}
                  </td>
                  <td>
                    {r.status !== 'paid' && (
                      <div className="row-actions">
                        <button className="btn sm" title={t('குரல் நினைவூட்டல்', 'Voice reminder')} disabled={voice_m.isPending} onClick={() => voice_m.mutate(r)}>
                          <Icon.wa />
                        </button>
                        {r.subscriber && (
                          <NotifyMenu
                            subscriber_id={r.subscriber.id}
                            intent={r.status === 'overdue' ? 'overdue_alert' : r.status === 'partial' ? 'partial_followup' : 'due_reminder'}
                            context={{ subscription_id: r.id, chit_id: r.chit_id }}
                          />
                        )}
                        <button className="btn sm primary" onClick={() => set_pay_target(r)}>
                          <Icon.check /> {t('actions.mark_paid', 'Collect payment')}
                        </button>
                      </div>
                    )}
                    {r.status === 'paid' && (
                      <button className="btn sm ghost" title="Receipt"><Icon.receipt /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {voice_toast && (
        <div className="fixed bottom-6 right-6 bg-info text-white px-4 py-3 rounded-lg shadow-lg max-w-sm z-50">
          <div className="text-sm">📞 {t('குரல் நினைவூட்டல் அனுப்பப்பட்டது', 'Voice reminder placed for')} {voice_toast.name}</div>
          {voice_toast.listen_url && (
            <a href={voice_toast.listen_url} target="_blank" rel="noreferrer" className="text-[11px] underline mt-1 inline-block">
              {t('நேரடி கேளுங்கள்', 'Listen live')}
            </a>
          )}
        </div>
      )}
      {voice_m.error && (
        <div className="fixed bottom-6 right-6 bg-bad-soft text-bad px-4 py-3 rounded-lg shadow-lg max-w-sm z-50">
          {friendly_error(voice_m.error)}
        </div>
      )}

      {pay_target && (
        <MarkPaidModal
          subscription_id={pay_target.id}
          expected_paise={pay_target.expected_amount_paise}
          total_paid_paise={pay_target.total_paid_paise}
          subscriber_name={(lang === 'ta' ? pay_target.subscriber?.name_ta : pay_target.subscriber?.name_en) ?? ''}
          {...(pay_target.subscriber?.id ? { subscriber_id: pay_target.subscriber.id } : {})}
          on_close={() => set_pay_target(null)}
        />
      )}
    </div>
  );
}
